import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, API } from "@/lib/api";
import { can, rupees } from "@/lib/helpers";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, DownloadSimple, PencilSimple } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { category: "Rent", amount: "", expense_date: today(), paid_to: "", payment_mode: "cash", notes: "" };

export function DateFilters({ f, setF, children }) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <div><Label className="text-xs text-[#6B6B6B]">From</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="filter-start" /></div>
      <div><Label className="text-xs text-[#6B6B6B]">To</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="filter-end" /></div>
      {children}
    </div>
  );
}

export async function downloadFile(path, name) {
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("og_token")}` } });
  if (!res.ok) { toast.error("Download failed"); return; }
  const a = document.createElement("a");
  a.href = URL.createObjectURL(await res.blob()); a.download = name; a.click();
}

export default function Expenses() {
  const { user, activeBranch } = useAuth();
  const [cats, setCats] = useState([]);
  const [d, setD] = useState({ rows: [], total: 0, by_category: [] });
  const [f, setF] = useState({ start: "", end: "", category: "" });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
    if (activeBranch) p.set("branch_id", activeBranch);
    return p.toString();
  }, [f, activeBranch]);
  const load = useCallback(() => api.get(`/expenses?${qs()}`).then(({ data }) => setD(data)).catch(() => {}), [qs]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/expenses/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter the amount");
    if (!form.expense_date) return toast.error("Select the date");
    const body = { ...form, amount: Number(form.amount), branch_id: activeBranch };
    try {
      if (editId) await api.put(`/expenses/${editId}`, body); else await api.post("/expenses", body);
      toast.success(editId ? "Expense updated" : "Expense added"); setOpen(false); setEditId(null); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const edit = (r) => { setEditId(r.id); setForm({ category: r.category, amount: String(r.amount), expense_date: r.expense_date.slice(0, 10), paid_to: r.paid_to || "", payment_mode: r.payment_mode || "cash", notes: r.notes || "" }); setOpen(true); };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Expenses" subtitle="Shop expenditure — rent, electricity, salaries, water and more.">
        <Button variant="outline" className="border-[#E6E2D6]" onClick={() => downloadFile(`/expenses/export?${qs()}`, "expenses_report.csv")} data-testid="expense-export-btn"><DownloadSimple size={16} className="mr-1" /> Export Report</Button>
        {can(user, "expenses", "create") && <Button onClick={() => { setEditId(null); setForm(EMPTY); setOpen(true); }} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-expense-btn"><Plus size={16} className="mr-1" /> Add Expense</Button>}
      </PageHeader>

      <DateFilters f={f} setF={setF}>
        <div>
          <Label className="text-xs text-[#6B6B6B]">Category</Label>
          <Select value={f.category || "all"} onValueChange={(v) => setF({ ...f, category: v === "all" ? "" : v })}>
            <SelectTrigger className="mt-1 w-48 bg-white border-[#E6E2D6]" data-testid="filter-category"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {(f.start || f.end || f.category) && <Button variant="ghost" onClick={() => setF({ start: "", end: "", category: "" })} data-testid="filter-clear">Clear</Button>}
      </DateFilters>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Card className="p-4 bg-[#1A1A1A] text-white border-0" data-testid="expense-total"><div className="text-[10px] uppercase tracking-wider text-[#C5A059]">Total ({d.count || 0})</div><div className="text-xl font-medium tabular-nums mt-1">{rupees(d.total)}</div></Card>
        {d.by_category.map((c) => (
          <Card key={c.category} className="p-4 bg-white border-[#E6E2D6]" data-testid={`expense-cat-${c.category.replace(/\s/g, "-").toLowerCase()}`}><div className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">{c.category}</div><div className="text-lg font-medium tabular-nums mt-1">{rupees(c.amount)}</div></Card>
        ))}
      </div>

      <Card className="bg-white border-[#E6E2D6] shadow-sm overflow-x-auto">
        {d.rows.length === 0 ? <EmptyState label="No expenses recorded" /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Paid To</TableHead><TableHead>Mode</TableHead><TableHead>Notes</TableHead><TableHead>Added By</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{d.rows.map((r) => (
              <TableRow key={r.id} data-testid={`expense-row-${r.id}`}>
                <TableCell className="text-sm">{r.expense_date.slice(0, 10)}</TableCell><TableCell className="font-medium">{r.category}</TableCell><TableCell className="text-sm">{r.paid_to || "-"}</TableCell><TableCell className="text-sm capitalize">{r.payment_mode || "-"}</TableCell><TableCell className="text-sm max-w-[220px] truncate">{r.notes || "-"}</TableCell><TableCell className="text-xs text-[#8A8A8A]">{r.created_by}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">{rupees(r.amount)}</TableCell>
                <TableCell className="flex gap-1">
                  {can(user, "expenses", "edit") && <Button size="icon" variant="ghost" onClick={() => edit(r)} data-testid={`expense-edit-${r.id}`}><PencilSimple size={15} /></Button>}
                  {can(user, "expenses", "delete") && <Button size="icon" variant="ghost" className="text-[#9D2A2A]" onClick={() => remove(r.id)} data-testid={`expense-delete-${r.id}`}><Trash size={15} /></Button>}
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit Expense" : "Add Expense"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="ef-category"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-[#6B6B6B]">Amount ₹ *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="ef-amount" /></div>
            <div><Label className="text-xs text-[#6B6B6B]">Date *</Label><Input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="ef-date" /></div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid="ef-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{["cash", "upi", "bank", "card", "cheque"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">Paid To</Label><Input value={form.paid_to} onChange={(e) => setForm({ ...form, paid_to: e.target.value })} placeholder="e.g. landlord / employee name" className="mt-1 bg-white border-[#E6E2D6]" data-testid="ef-paid-to" /></div>
            <div className="col-span-2"><Label className="text-xs text-[#6B6B6B]">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid="ef-notes" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="ef-save">Save Expense</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
